import { getVersion } from "@tauri-apps/api/app";
import { relaunch as relaunchProcess } from "@tauri-apps/plugin-process";
import { check, type Update } from "@tauri-apps/plugin-updater";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import { useAuthUser } from "src/auth/useAuth";
import { progressFromDownloadEvent } from "src/lib/desktopUpdate";
import { isMainWindow, isTauriRuntime } from "src/lib/runtime/environment";
import { useApplicationPendingChangesStore } from "src/stores/applicationPendingChanges";

const UPDATE_CHECK_THROTTLE_MS = 5 * 60 * 1000;
const INITIAL_CHECK_DELAY_MS = 1_500;

export type DesktopUpdatePhase =
  | "idle"
  | "checking"
  | "available"
  | "downloading"
  | "readyToRelaunch"
  | "error";

interface DesktopUpdateState {
  phase: DesktopUpdatePhase;
  currentVersion?: string;
  availableVersion?: string;
  progress?: number;
  error?: string;
  lastCheckedAt?: number;
}

interface DesktopAppUpdateContextValue extends DesktopUpdateState {
  enabled: boolean;
  checkForUpdate: () => Promise<void>;
  installUpdate: () => Promise<void>;
  relaunch: () => Promise<void>;
}

const initialState: DesktopUpdateState = { phase: "idle" };

const DesktopAppUpdateContext = createContext<DesktopAppUpdateContextValue>({
  ...initialState,
  enabled: false,
  checkForUpdate: async () => undefined,
  installUpdate: async () => undefined,
  relaunch: async () => undefined,
});

const updaterEnabledForBuild = import.meta.env.VITE_DESKTOP_UPDATES_ENABLED === "true";

const messageForError = (error: unknown): string =>
  error instanceof Error ? error.message : "The desktop update could not be completed.";

export const DesktopAppUpdateProvider = ({ children }: { children: ReactNode }) => {
  const { token, user } = useAuthUser();
  const enabled =
    updaterEnabledForBuild && isTauriRuntime() && isMainWindow() && !!token && !!user?.internalEmployee;
  const [state, setState] = useState<DesktopUpdateState>(initialState);
  const updateRef = useRef<Update | null>(null);
  const operationRef = useRef(false);
  const lastCheckedAtRef = useRef(0);
  const phaseRef = useRef<DesktopUpdatePhase>("idle");

  useEffect(() => {
    phaseRef.current = state.phase;
  }, [state.phase]);

  const closeUpdate = useCallback(async () => {
    const update = updateRef.current;
    updateRef.current = null;
    if (update) await update.close().catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!isTauriRuntime()) return;
    void getVersion()
      .then((currentVersion) => setState((current) => ({ ...current, currentVersion })))
      .catch(() => undefined);
  }, []);

  const checkForUpdate = useCallback(async () => {
    if (!enabled || !token || operationRef.current) return;
    operationRef.current = true;
    setState((current) => ({ ...current, phase: "checking", error: undefined }));

    try {
      await closeUpdate();
      const update = await check({ headers: { Authorization: `Bearer ${token}` } });
      const checkedAt = Date.now();
      lastCheckedAtRef.current = checkedAt;
      updateRef.current = update;
      setState((current) => ({
        ...current,
        phase: update ? "available" : "idle",
        currentVersion: update?.currentVersion ?? current.currentVersion,
        availableVersion: update?.version,
        progress: undefined,
        error: undefined,
        lastCheckedAt: checkedAt,
      }));
    } catch (error) {
      const checkedAt = Date.now();
      lastCheckedAtRef.current = checkedAt;
      setState((current) => ({
        ...current,
        phase: "error",
        error: messageForError(error),
        lastCheckedAt: checkedAt,
      }));
    } finally {
      operationRef.current = false;
    }
  }, [closeUpdate, enabled, token]);

  const installUpdate = useCallback(async () => {
    if (!enabled || !token || operationRef.current) return;
    operationRef.current = true;
    setState((current) => ({ ...current, phase: "checking", error: undefined }));

    try {
      // The feed returns a short-lived S3 URL, so always refresh it immediately before download.
      await closeUpdate();
      const update = await check({ headers: { Authorization: `Bearer ${token}` } });
      if (!update) {
        const checkedAt = Date.now();
        lastCheckedAtRef.current = checkedAt;
        setState((current) => ({
          ...current,
          phase: "idle",
          availableVersion: undefined,
          lastCheckedAt: checkedAt,
        }));
        return;
      }

      updateRef.current = update;
      setState((current) => ({
        ...current,
        phase: "downloading",
        currentVersion: update.currentVersion,
        availableVersion: update.version,
        progress: undefined,
      }));
      const transferred: { bytes: number; total?: number } = { bytes: 0 };
      // The feed's Authorization header is otherwise reused for the artifact download, and the feed
      // hands back a presigned S3 URL: S3 rejects a presigned request that also carries an
      // Authorization header with 400 InvalidArgument. An empty map replaces the inherited headers.
      await update.downloadAndInstall(
        (event) => {
          const progress = progressFromDownloadEvent(event, transferred);
          setState((current) => ({ ...current, phase: "downloading", progress }));
        },
        { headers: {} }
      );
      await closeUpdate();
      setState((current) => ({ ...current, phase: "readyToRelaunch", progress: 100 }));
    } catch (error) {
      await closeUpdate();
      setState((current) => ({ ...current, phase: "error", error: messageForError(error) }));
    } finally {
      operationRef.current = false;
    }
  }, [closeUpdate, enabled, token]);

  const relaunch = useCallback(async () => {
    const pending = useApplicationPendingChangesStore.getState();
    if (pending.saveState === "saving" || Object.keys(pending.changes).length > 0) {
      toast.error("Save or discard the pending deal changes before relaunching.");
      return;
    }
    if (!window.confirm("Relaunch Fraction LOS now? All desktop windows will close.")) return;

    try {
      await relaunchProcess();
    } catch (error) {
      const message = messageForError(error);
      setState((current) => ({ ...current, phase: "error", error: message }));
      toast.error(message);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      void closeUpdate();
      setState((current) => ({ ...current, phase: "idle", availableVersion: undefined }));
      return;
    }

    const maybeCheck = () => {
      if (phaseRef.current !== "idle" && phaseRef.current !== "error") return;
      if (Date.now() - lastCheckedAtRef.current < UPDATE_CHECK_THROTTLE_MS) return;
      void checkForUpdate();
    };
    const timer = window.setTimeout(maybeCheck, INITIAL_CHECK_DELAY_MS);
    window.addEventListener("focus", maybeCheck);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("focus", maybeCheck);
    };
  }, [checkForUpdate, closeUpdate, enabled]);

  useEffect(() => () => void closeUpdate(), [closeUpdate]);

  const value = useMemo<DesktopAppUpdateContextValue>(
    () => ({ ...state, enabled, checkForUpdate, installUpdate, relaunch }),
    [checkForUpdate, enabled, installUpdate, relaunch, state]
  );

  return <DesktopAppUpdateContext.Provider value={value}>{children}</DesktopAppUpdateContext.Provider>;
};

export const useDesktopAppUpdate = () => useContext(DesktopAppUpdateContext);
